import { useRef } from "react";
import { Check } from "lucide-react";
import { FILL, BAND_LABEL, computeBand, bandIndex, BANDS } from "@/lib/progressBand";

/*
  Shared DEVELOPMENT METER for Composition + Sentence Craft — one coherent visual language.
  A salient horizontal track that answers "am I moving forward, and where does this stage end?" WITHOUT a
  displayed percentage. It advances only on genuine resolved-target movement (props), and is clamped
  MONOTONIC so ordinary new-target discovery never bounces it backward (the treadmill fix). Readiness is
  the explicit endpoint (READY FOR SENTENCE CRAFT / READY FOR PROOFREADING) and, once reached, stays reached
  even during voluntary optional refinement.
*/
const TERRA = "#8C3A2A";

export default function DevelopmentMeter({ resolved = 0, budget = 0, ready = false, startLabel, endLabel }) {
  const band = computeBand({ resolved, budget, ready });
  // Monotonic clamp: never show a lower band than already reached (unless readiness itself is set, which
  // always wins). Honest — the learner really did that work; discovering more needs doesn't undo it.
  const maxIdx = useRef(0);
  let idx = bandIndex(band);
  if (ready) idx = bandIndex("ready");
  else if (idx < maxIdx.current) idx = maxIdx.current;
  maxIdx.current = Math.max(maxIdx.current, idx);
  const shownBand = BANDS[idx];
  const fill = FILL[shownBand];
  const atReady = shownBand === "ready";

  return (
    <div data-testid="development-meter" data-band={shownBand}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500">{BAND_LABEL[shownBand]}</span>
        {atReady && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700" data-testid="development-meter-ready">
            <Check className="h-3 w-3" strokeWidth={3} /> {endLabel}
          </span>
        )}
      </div>

      {/* The track */}
      <div className="relative h-3 w-full rounded-full bg-stone-200/80 overflow-hidden" aria-hidden="true">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-out"
          style={{ width: `${fill}%`, background: atReady ? "#059669" : `linear-gradient(90deg, ${TERRA}, #b6564180)` }}
          data-testid="development-meter-fill"
        />
        {/* position marker */}
        <div
          className="absolute top-1/2 h-4 w-4 -translate-y-1/2 -translate-x-1/2 rounded-full border-2 border-white shadow"
          style={{ left: `${fill}%`, background: atReady ? "#059669" : TERRA }}
        />
      </div>

      {/* endpoints */}
      <div className="mt-1.5 flex items-center justify-between text-[10px] uppercase tracking-[0.12em]">
        <span className="text-stone-400">{startLabel}</span>
        <span className={atReady ? "font-semibold text-emerald-700" : "text-stone-400"}>{endLabel}</span>
      </div>
    </div>
  );
}
